/**
 * HrvAnalysisPanel — iter633 — Професионален HRV анализ чрез NeuroKit2.
 * Lazy-mounted в CardioTwinPage. Приема RR интервали (паста или manual entry).
 */
import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Loader2, Activity, Heart } from "lucide-react";
import { toast } from "sonner";

export default function HrvAnalysisPanel({ onAnalyzed }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [rrText, setRrText] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  const analyze = async () => {
    const parts = rrText
      .replace(/[\n;]/g, ",")
      .split(",")
      .map(s => s.trim())
      .filter(Boolean)
      .map(parseFloat)
      .filter(n => !isNaN(n) && n > 200 && n < 2000);
    if (parts.length < 30) {
      toast.error(`Нужни са ≥30 RR интервала (имаш ${parts.length}).`);
      return;
    }
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/hrv/analyze`, {
        rr_intervals_ms: parts, source: "manual",
      }, { timeout: 30000 });
      setResult(data);
      toast.success(`HRV scor: ${data.score}/100`);
      window.dispatchEvent(new Event("cardio-log-changed"));
      window.dispatchEvent(new Event("twin-score-changed"));
      if (onAnalyzed) onAnalyzed(data);
    } catch (e) {
      toast.error("Грешка при HRV анализ.");
    } finally {
      setAnalyzing(false);
    }
  };

  const sampleData = () => {
    // Generate 60 realistic RR intervals
    const rrs = [];
    for (let i = 0; i < 60; i++) {
      rrs.push(Math.round(820 + Math.random() * 80 - 40));
    }
    setRrText(rrs.join(", "));
  };

  const cardCls = isDark ? "bg-slate-900/40 border-slate-700" : "bg-white border-slate-200";

  return (
    <div className={`p-4 rounded-2xl border-2 ${isDark ? "bg-rose-900/20 border-rose-700/30" : "bg-rose-50 border-rose-200"}`} data-testid="hrv-panel">
      <div className="flex items-center gap-2 mb-2">
        <Heart className="w-4 h-4 text-rose-500" />
        <h3 className="text-sm font-bold">🫀 Професионален HRV анализ (NeuroKit2)</h3>
      </div>
      <p className="text-[11px] opacity-80 mb-3">
        Пасни RR интервали (мс) от Apple Health/Garmin/Polar или Whoop. Минимум 30 удара.
        Системата изчислява RMSSD, SDNN, LF/HF, SD1/SD2 + автоматично актуализира Кардио близнака.
      </p>

      <textarea
        value={rrText}
        onChange={(e) => setRrText(e.target.value)}
        placeholder="820, 840, 810, 860, 830, 850, ..."
        rows={3}
        data-testid="hrv-rr-input"
        className={`w-full px-2 py-1.5 rounded-md border text-xs font-mono ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`}
      />
      <div className="flex gap-2 mt-2">
        <Button onClick={analyze} disabled={analyzing} className="bg-rose-600 hover:bg-rose-700 text-white flex-1" data-testid="hrv-analyze-btn">
          {analyzing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Activity className="w-4 h-4 mr-2" />}
          Анализирай HRV
        </Button>
        <Button onClick={sampleData} variant="outline" size="sm" data-testid="hrv-sample-btn">
          📋 Тестови данни
        </Button>
      </div>

      {result && (
        <div className={`mt-3 p-3 rounded-lg ${cardCls}`} data-testid="hrv-result">
          <div className="flex items-baseline gap-3 mb-2">
            <span className={`text-2xl font-bold ${
              (result.score ?? 0) >= 80 ? "text-emerald-500"
              : (result.score ?? 0) >= 60 ? "text-amber-500"
              : "text-rose-500"
            }`} data-testid="hrv-result-score">{result.score ?? "—"}/100</span>
            <span className="text-xs opacity-60">HRV scor</span>
          </div>
          <p className="text-xs leading-relaxed mb-2" data-testid="hrv-result-interpretation">{result.interpretation_bg}</p>
          {result.flags_bg && result.flags_bg.length > 0 && (
            <ul className="text-[10px] opacity-80 space-y-0.5 pl-3 list-disc mb-2">
              {result.flags_bg.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          )}
          <details className="text-[10px] opacity-70">
            <summary className="cursor-pointer">Пълен HRV панел</summary>
            <div className="mt-1 grid grid-cols-2 gap-1 font-mono">
              {[
                ["RMSSD", result.panel?.rmssd_ms, "ms"],
                ["SDNN",  result.panel?.sdnn_ms,  "ms"],
                ["pNN50", result.panel?.pnn50_pct, "%"],
                ["Mean HR", result.panel?.mean_hr_bpm, "bpm"],
                ["LF/HF", result.panel?.lf_hf, ""],
                ["SD1",   result.panel?.sd1_ms, "ms"],
                ["SD2",   result.panel?.sd2_ms, "ms"],
                ["SampEn", result.panel?.sample_entropy, ""],
              ].map(([k, v, u]) => (
                <div key={k}>{k}: {v ?? "—"} {u}</div>
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}
